import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTooth, 
    faAngleDown, 
    faAngleRight, 
    faExclamationTriangle,
    faInfoCircle,
    faChartBar
} from '@fortawesome/free-solid-svg-icons';
import './OralHealthCard.css';

/**
 * Specialized component for rendering Oral Health analysis data
 */
const OralHealthCard = ({ data, animationDelay }) => {
    // Debug logging
    console.log('Oral Health Card received data:', data);
    
    const [expanded, setExpanded] = useState(false);
    const [activeSection, setActiveSection] = useState('diversity');
    
    // Early return if no data
    if (!data) {
        console.error('Oral Health Card received no data');
        return null;
    }
    
    // Get status info to color elements
    const statusColor = data.status?.toLowerCase() === 'warning' ? '#ff9800' : 
                      data.status?.toLowerCase() === 'negative' ? '#f44336' : 
                      data.status?.toLowerCase() === 'positive' ? '#64b5f6' : '#9e9e9e';
    
    // Access the details object safely
    const details = data.details || {};
    
    // Extract diversity scores
    const diversityScores = details.diversity_scores || {};
    const hasDiversityData = Object.keys(diversityScores).length > 0;
    
    // Extract disease risk assessment
    const diseaseRisk = details.disease_risk_assessment || {};
    const highRiskDiseases = diseaseRisk.potential_concern_diseases || [];
    const hasDiseaseData = highRiskDiseases.length > 0;
    
    console.log('Oral Health data availability:', { 
        hasDiversityData, 
        hasDiseaseData
    });
    
    // Calculate summary information
    const getDiversitySummary = () => {
        if (!hasDiversityData) return null;
        
        // Count diversity issues by status
        const diversityIssues = Object.values(diversityScores).filter(score => score.status === 'Low' || score.status === 'High').length;
        if (diversityIssues > 0) {
            return {
                count: diversityIssues,
                text: `${diversityIssues} diversity ${diversityIssues === 1 ? 'metric' : 'metrics'} outside optimal range`,
                isWarning: true
            };
        }
        return null;
    };
    
    const getDiseaseSummary = () => {
        if (!hasDiseaseData) return null;
        
        // Count serious disease risks (score >= 0.7)
        const seriousRisks = highRiskDiseases.filter(disease => disease.score >= 0.7).length;
        if (seriousRisks > 0) {
            return {
                count: seriousRisks,
                text: `${seriousRisks} elevated health ${seriousRisks === 1 ? 'risk' : 'risks'}`,
                isWarning: true
            };
        }
        return { 
            count: highRiskDiseases.length,
            text: `${highRiskDiseases.length} moderate health ${highRiskDiseases.length === 1 ? 'risk' : 'risks'}`,
            isWarning: false
        };
    };
    
    // Get all summaries
    const diversitySummary = getDiversitySummary();
    const diseaseSummary = getDiseaseSummary();
    
    // Collect all summaries for display
    const summaries = [diversitySummary, diseaseSummary].filter(Boolean);
    
    // Build report summary counts for the top summary display
    const getReportCounts = () => {
        // Get diversity, bacteria, and disease risk counts
        const lowDiversityMetrics = Object.values(diversityScores).filter(score => score.status === 'Low').length;
        const highDiversityMetrics = Object.values(diversityScores).filter(score => score.status === 'High').length;
        
        // Classify disease risks by severity
        const highRisks = highRiskDiseases.filter(disease => disease.score >= 0.7).length;
        const moderateRisks = highRiskDiseases.filter(disease => disease.score >= 0.5 && disease.score < 0.7).length;
        const lowRisks = highRiskDiseases.filter(disease => disease.score < 0.5).length;
        
        return {
            abnormalDiversityMetrics: lowDiversityMetrics + highDiversityMetrics,
            highRisks,
            moderateRisks,
            lowRisks
        };
    };
    
    const reportCounts = getReportCounts();

    return (
        <div className="s2-oral-card" style={{ animationDelay }}>
            <div className="s2-oral-header" onClick={() => setExpanded(!expanded)}>
                <div className="s2-oral-icon">
                    <FontAwesomeIcon 
                        icon={faTooth} 
                        className="s2-oral-icon-tooth"
                    />
                </div>
                <div className="s2-oral-title-container">
                    <div className="s2-oral-title">Oral Microbiome Analysis</div>
                    <div className="s2-oral-status">
                        {data.status === 'Warning' && (
                            <FontAwesomeIcon 
                                icon={faExclamationTriangle} 
                                className="s2-oral-warning-icon"
                                style={{ color: '#ff9800', marginRight: '0.5vh' }}
                            />
                        )}
                        {data.status === 'Negative' && (
                            <FontAwesomeIcon 
                                icon={faExclamationTriangle} 
                                className="s2-oral-warning-icon"
                                style={{ color: '#f44336', marginRight: '0.5vh' }}
                            />
                        )}
                        <span style={{ color: statusColor }}>{data.status }</span>
                    </div>
                    
                    {/* Oral Health Summary Section */}
                    {summaries.length > 0 && (
                        <div className="s2-oral-summary">
                            {summaries.map((summary, index) => (
                                <div 
                                    key={index} 
                                    className={`s2-oral-pill ${summary.isWarning ? 's2-oral-pill-warning' : ''}`}
                                >
                                    {summary.text}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="s2-oral-expand-icon">
                    <FontAwesomeIcon 
                        icon={expanded ? faAngleDown : faAngleRight} 
                    />
                </div>
            </div>
            
            {expanded && (
                <div className="s2-oral-content s2-anim-fadeIn">
                    {/* WGS-style Summary Section */}
                    <div className="s2-oral-wgs-summary">
                        <div className="s2-oral-summary-row">
                            <div className="s2-oral-summary-item">
                                <div className="s2-oral-summary-label">Diversity Metrics</div>
                                <div className="s2-oral-summary-value">
                                    <span className={reportCounts.abnormalDiversityMetrics > 0 ? 's2-oral-negative' : ''}>
                                        {reportCounts.abnormalDiversityMetrics} abnormal
                                    </span>
                                    {' / '}{Object.keys(diversityScores).length} total
                                </div>
                            </div>
                            
                            <div className="s2-oral-summary-item">
                                <div className="s2-oral-summary-label">Health Risk Assessment</div>
                                <div className="s2-oral-summary-value">
                                    {reportCounts.highRisks > 0 && (
                                        <span className="s2-oral-negative">{reportCounts.highRisks} high</span>
                                    )}
                                    {reportCounts.highRisks > 0 && reportCounts.moderateRisks > 0 && ', '}
                                    {reportCounts.moderateRisks > 0 && (
                                        <span className="s2-oral-warning">{reportCounts.moderateRisks} moderate</span>
                                    )}
                                    {(reportCounts.highRisks > 0 || reportCounts.moderateRisks > 0) && reportCounts.lowRisks > 0 && ', '}
                                    {reportCounts.lowRisks > 0 && (
                                        <span>{reportCounts.lowRisks} low</span>
                                    )}
                                    {reportCounts.highRisks === 0 && reportCounts.moderateRisks === 0 && reportCounts.lowRisks === 0 && (
                                        <span>No risks detected</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Tabs navigation */}
                    <div className="s2-oral-tabs">
                        <div 
                            className={`s2-oral-tab ${activeSection === 'diversity' ? 's2-oral-tab-active' : ''}`}
                            onClick={() => setActiveSection('diversity')}
                        >
                            <FontAwesomeIcon icon={faChartBar} className="s2-oral-tab-icon" />
                            <span>Diversity</span>
                        </div>
                        <div 
                            className={`s2-oral-tab ${activeSection === 'diseases' ? 's2-oral-tab-active' : ''}`}
                            onClick={() => setActiveSection('diseases')}
                        >
                            <FontAwesomeIcon icon={faTooth} className="s2-oral-tab-icon" />
                            <span>Health Risks</span>
                        </div>
                    </div>
                    
                    {/* Diversity Scores Section */}
                    {activeSection === 'diversity' && hasDiversityData && (
                        <div className="s2-oral-diversity-section">
                            <h3 className="s2-oral-section-title">Microbiome Diversity Metrics</h3>
                            <div className="s2-oral-diversity-grid">
                                {Object.entries(diversityScores).map(([key, value]) => {
                                    const displayName = key
                                        .replace('_', ' ')
                                        .split(' ')
                                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                        .join(' ');
                                    
                                    const statusColorMap = {
                                        'Low': '#f44336',
                                        'Normal': '#4caf50',
                                        'High': '#ff9800'
                                    };
                                    
                                    const metricColor = value.status === 'Normal' ? '#64b5f6' : statusColorMap[value.status] || '#9e9e9e';
                                    
                                    return (
                                        <div key={key} className="s2-oral-diversity-metric">
                                            <div className="s2-oral-diversity-metric-name">{displayName}</div>
                                            <div className="s2-oral-diversity-metric-value">{value.score}</div>
                                            <div className="s2-oral-diversity-metric-status" style={{ color: metricColor }}>
                                                {value.status}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="s2-oral-diversity-explanation">
                                <p><strong>Richness:</strong> Number of different bacterial species in your oral microbiome.</p>
                                <p><strong>Shannon Diversity:</strong> Measures both richness and evenness of bacteria distribution.</p>
                                <p><strong>Pielou Evenness:</strong> How equally distributed different bacteria are in your oral microbiome.</p>
                                <p><strong>F/B Ratio:</strong> Balance between Firmicutes and Bacteroidetes phyla.</p>
                            </div>
                        </div>
                    )}
                    
                    {/* Health Risk Section */}
                    {activeSection === 'diseases' && hasDiseaseData && (
                        <div className="s2-oral-disease-section">
                            <h3 className="s2-oral-section-title">Health Risk Assessment</h3>
                            <div className="s2-oral-disease-list">
                                {highRiskDiseases.map((disease, index) => {
                                    // Format the disease name for better display
                                    const formattedName = disease.disease_name
                                        .replace(/\./g, ' - ')
                                        .split(' ')
                                        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                        .join(' ');
                                    
                                    // Calculate the risk bar width as percentage
                                    const riskPercentage = Math.round(disease.score * 100);
                                    
                                    return (
                                        <div key={index} className="s2-oral-disease-item">
                                            <div className="s2-oral-disease-header">
                                                <div className="s2-oral-disease-name">{formattedName}</div>
                                                <div className="s2-oral-disease-score">{riskPercentage}%</div>
                                            </div>
                                            
                                            <div className="s2-oral-disease-risk-bar-container">
                                                <div 
                                                    className="s2-oral-disease-risk-bar" 
                                                    style={{ width: `${riskPercentage}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    
                    {/* Fallback display if no data is available for the selected section */}
                    {activeSection === 'diversity' && !hasDiversityData && (
                        <div className="s2-oral-no-data">No diversity data available</div>
                    )}
                    {activeSection === 'diseases' && !hasDiseaseData && (
                        <div className="s2-oral-no-data">No disease risk assessment available</div>
                    )}
                </div>
            )}
            
            {data.source && (
                <div className="s2-oral-footer">
                    <span className="s2-oral-source">
                        <FontAwesomeIcon icon={faInfoCircle} className="s2-oral-source-icon" />
                        {data.source}
                    </span>
                </div>
            )}
        </div>
    );
};

export default OralHealthCard;
